import React, { useEffect, useState, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useScreenInit } from './useScreenInit.js';
import { PresentationFrame } from './components/PresentationFrame';
import { MinervaLogo } from './components/MinervaLogo';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Slide1Overview } from './slides/Slide1Overview';
import { Slide2LeadGen } from './slides/Slide2LeadGen';
import { Slide3BrandEngagement } from './slides/Slide3BrandEngagement';
import { Slide4MarketResearch } from './slides/Slide4MarketResearch';
import { Slide5DualOpportunity } from './slides/Slide5DualOpportunity';
import { SlideViewerResponse } from './slides/SlideViewerResponse';
import { Slide6Expansion } from './slides/Slide6Expansion';
import { Slide7Summary } from './slides/Slide7Summary';
import { SnakkerLogo } from './components/SnakkerLogo';
const SLIDES = [Slide1Overview, Slide2LeadGen, Slide3BrandEngagement, Slide4MarketResearch, Slide5DualOpportunity, SlideViewerResponse, Slide6Expansion, Slide7Summary];
export function App() {
  const screenInit = useScreenInit();
  const [currentIndex, setCurrentIndex] = useState(typeof screenInit?.currentIndex === 'number' ? screenInit.currentIndex : 0);
  const nextSlide = () => {
    setCurrentIndex(prev => prev < SLIDES.length - 1 ? prev + 1 : prev);
  };
  const prevSlide = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : prev);
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  const CurrentSlideComponent = SLIDES[currentIndex];
  return <PresentationFrame data-id="element-0">
      {/* Report Header */}
      <div className="flex justify-between items-center px-12 py-6 border-b border-slate-100 bg-white z-20 shrink-0" data-id="element-1">
        <MinervaLogo className="h-8" data-id="element-2" />
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500" data-id="element-3">
          <span data-id="element-4">Report prepared by</span>
          <div className="h-4 w-px bg-slate-200" data-id="element-5" />
          <SnakkerLogo className="h-6" data-id="element-6" />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 relative overflow-y-auto overflow-x-hidden show-scroll bg-slate-50/30" data-id="element-7">
        <AnimatePresence mode="wait" data-id="element-8">
          <motion.div key={currentIndex} initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -15
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} className="w-full min-h-full flex flex-col pt-10 pb-16 px-12" data-id="element-9">
            <CurrentSlideComponent data-id="element-10" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Side Navigation Arrows */}
      <button onClick={prevSlide} disabled={currentIndex === 0} aria-label="Previous section" className={`absolute left-4 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-white/90 backdrop-blur shadow-md border border-slate-200 transition-all z-50 ${currentIndex === 0 ? 'opacity-0 pointer-events-none' : 'hover:bg-slate-50 hover:scale-105 text-slate-600'}`} data-id="element-11">
        <ChevronLeft className="w-5 h-5" data-id="element-12" />
      </button>
      <button onClick={nextSlide} disabled={currentIndex === SLIDES.length - 1} aria-label="Next section" className={`absolute right-4 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-snakker-purple/95 backdrop-blur shadow-md text-white transition-all z-50 ${currentIndex === SLIDES.length - 1 ? 'opacity-0 pointer-events-none' : 'hover:bg-snakker-dark hover:scale-105'}`} data-id="element-13">
        <ChevronRight className="w-5 h-5" data-id="element-14" />
      </button>

      {/* Report Footer */}
      <div className="flex items-center justify-between px-12 py-4 border-t border-slate-100 bg-white text-xs font-medium text-slate-400 z-20 shrink-0" data-id="element-15">
        <div className="flex items-center gap-2" data-id="element-16">
          <span className="uppercase tracking-wider text-slate-500 font-bold" data-id="element-17">
            Confidential
          </span>
          <span data-id="element-18">•</span>
          <span data-id="element-19">Prepared for Minerva Leadership</span>
        </div>

        <div className="flex gap-1" data-id="element-20">
          {SLIDES.map((_, i) => <button key={i} onClick={() => setCurrentIndex(i)} aria-label={`Go to section ${i + 1}`} className="group py-2 px-1 flex items-center" data-id="element-21">
              <span className={`block h-1.5 rounded-full transition-all ${i === currentIndex ? 'w-6 bg-snakker-purple' : 'w-1.5 bg-slate-200 group-hover:bg-slate-300'}`} data-id="element-22" />
            </button>)}
        </div>

        <div data-id="element-23">June 2026</div>
      </div>
    </PresentationFrame>;
}